import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/shadcn/alert-dialog';

const CONFIRM_PHRASE = 'OVERWRITE';

// Confirmation gate shown when the user asks to overwrite a realty that
// carries manual edits from a previous user session. Forces typing
// "OVERWRITE" so the destructive action can't happen by mistake.
export const OverwriteGuardModal = ({ open, manualEditFields = [], onCancel, onConfirm }) => {
  const [phrase, setPhrase] = useState('');
  const canConfirm = phrase === CONFIRM_PHRASE;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setPhrase('');
          onCancel();
        }
      }}
    >
      <AlertDialogContent data-testid="overwrite-guard-modal" className="border-orangeColor/40">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-orangeColor">
            <AlertTriangle size={20} />
            Overwrite existing manual edits?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-blackColor">
            The backend realty has manual edits on the following fields. Overwriting will
            replace them with parser data and cannot be undone from this screen (an audit
            entry is still kept).
          </AlertDialogDescription>
        </AlertDialogHeader>

        <ul className="flex flex-wrap gap-2 my-2">
          {manualEditFields.map((f) => (
            <li
              key={f}
              className="flex items-center gap-1 px-2 py-1 rounded bg-orangeColor/10 text-orangeColor text-xs"
            >
              <AlertTriangle size={12} />
              {f.replace(/_/g, ' ')}
            </li>
          ))}
        </ul>

        <label className="flex flex-col gap-1 mt-2">
          <span className="text-xs text-darkGrayColor">
            Type <span className="font-mono font-semibold text-blackColor">{CONFIRM_PHRASE}</span> to confirm
          </span>
          <input
            data-testid="overwrite-guard-confirm-input"
            type="text"
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            placeholder={CONFIRM_PHRASE}
            className="px-3 py-2 text-sm border border-gray-200 rounded outline-none focus:border-orangeColor font-mono tracking-widest"
          />
        </label>

        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => {
              setPhrase('');
              onCancel();
            }}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="overwrite-guard-confirm-button"
            disabled={!canConfirm}
            className={
              'bg-orangeColor text-white hover:bg-orangeColor/90 ' +
              (!canConfirm ? 'opacity-50 cursor-not-allowed' : '')
            }
            onClick={() => {
              if (!canConfirm) return;
              setPhrase('');
              onConfirm();
            }}
          >
            Overwrite
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
